import { useState } from 'react';
import { useCart } from '../../../context/CartContext';

interface Product {
  id: number | string;
  name: string;
  price: number;
  originalPrice?: number;
  usdtPrice?: number;
  image: string;
  category: string;
  description: string;
  rating: number;
  reviews: number;
  discount?: number;
  stock: number;
  badge?: string;
  weight?: string;
}

interface ProductCardProps {
  product: Product;
  index?: number;
  loaded?: boolean;
  onViewDetails?: () => void;
  onClick?: () => void;
}

export default function ProductCard({ product, index = 0, loaded = true, onViewDetails, onClick }: ProductCardProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const { addToCart } = useCart();

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      id: String(product.id),
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      category: product.category,
    });
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleClick = () => {
    if (onViewDetails) onViewDetails();
    if (onClick) onClick();
  };

  return (
    <div
      onClick={handleClick}
      className={`group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-emerald-200 transition-all duration-500 cursor-pointer relative ${
        loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {product.discount && (
        <div className="absolute top-2 left-2 z-10 px-2 py-0.5 md:px-3 md:py-1 bg-red-500 text-white text-xs font-bold rounded-full">
          {product.discount}% OFF
        </div>
      )}

      {product.badge && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 md:px-3 md:py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full">
          {product.badge}
        </div>
      )}

      {!product.badge && product.stock <= 5 && product.stock > 0 && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 md:px-3 md:py-1 bg-red-600 text-white text-xs font-bold rounded-full animate-pulse">
          품절 임박 {product.stock}개
        </div>
      )}

      {product.stock === 0 && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 md:px-3 md:py-1 bg-gray-500 text-white text-xs font-bold rounded-full">
          품절
        </div>
      )}

      <div className="w-full h-40 md:h-64 overflow-hidden bg-gray-50">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
      </div>

      <div className="p-3 md:p-5">
        {product.weight && (
          <p className="text-[10px] text-gray-400 mb-1">{product.weight}</p>
        )}
        <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1.5 line-clamp-2 group-hover:text-emerald-600 transition-colors leading-snug">
          {product.name}
        </h3>

        <div className="flex items-center gap-0.5 mb-2">
          {[...Array(5)].map((_, i) => (
            <i
              key={i}
              className={`${
                i < Math.floor(product.rating)
                  ? 'ri-star-fill text-yellow-400'
                  : 'ri-star-line text-gray-300'
              } text-xs w-3 h-3 flex items-center justify-center`}
            ></i>
          ))}
          <span className="text-xs text-gray-500 ml-1">
            ({product.reviews})
          </span>
        </div>

        <div className="flex flex-col gap-1.5 mb-3">
          <div className="flex items-baseline gap-1.5">
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-xs text-gray-400 line-through">
                {formatPrice(product.originalPrice)}원
              </span>
            )}
            <span className="text-base md:text-xl font-bold text-emerald-600">
              {formatPrice(product.price)}원
            </span>
          </div>
          {product.usdtPrice && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold">
                <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                미화 USDT ${product.usdtPrice}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={handleAddToCart}
          className="w-full py-2 md:py-3 bg-emerald-600 text-white rounded-full text-xs md:text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap relative overflow-hidden cursor-pointer"
        >
          <span className={`transition-opacity duration-300 ${showSuccess ? 'opacity-0' : 'opacity-100'}`}>
            장바구니 담기
          </span>
          {showSuccess && (
            <span className="absolute inset-0 flex items-center justify-center">
              <i className="ri-check-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
